/**
 * useSpeechRecognition.js
 * Custom React hook for speech-to-text functionality in Constellation KG
 * 
 * Features:
 * - Single SpeechRecognition instance shared across the app
 * - Context-aware: knows which textarea to write to
 * - On-device recognition preference (Chrome 139+) for privacy
 * - Character limit enforcement with automatic stop
 * - Master enable/disable toggle for sidebar control
 * - Graceful handling of browser support and permissions
 * 
 * Usage:
 *   const { 
 *     isRecording, 
 *     isEnabled, 
 *     startRecording, 
 *     stopRecording 
 *   } = useSpeechRecognition();
 * 
 *   // In a component with a textarea:
 *   const textareaRef = useRef(null);
 *   <button onClick={() => startRecording(textareaRef, 3000)}>🎤</button>
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// =========================================
// Constants
// =========================================

/**
 * Permission states for microphone access
 */
export const PERMISSION_STATES = {
  GRANTED: 'granted',
  DENIED: 'denied',
  PROMPT: 'prompt',
  UNKNOWN: 'unknown'
};

/**
 * Status messages for UI feedback
 */
export const STATUS_TYPES = {
  IDLE: 'idle',
  RECORDING: 'recording',
  ERROR: 'error',
  INITIALIZING: 'initializing'
};

/**
 * On-device recognition verdict.
 *
 * THE RULING (Dennis, October 12, 2026): dictation runs on the user's device or
 * not at all. CKG never lets a user's speech go to a browser vendor's cloud
 * speech service. Where on-device recognition cannot be guaranteed, dictation is
 * refused with a plain message.
 *
 * This enum is the verdict that decides it. Only CONFIRMED permits recognition
 * to start. UNKNOWN is the pre-probe state and is treated as "not yet allowed",
 * never as "allowed" — the whole point is that the default is refusal.
 */
export const ON_DEVICE_STATES = {
  UNKNOWN: 'unknown',         // not probed yet — dictation not permitted
  CHECKING: 'checking',       // probe in flight
  INSTALLABLE: 'installable', // a language pack is available to download
  PREPARING: 'preparing',     // the language pack is downloading now
  CONFIRMED: 'confirmed',     // on-device recognition confirmed for this language

  // THE THREE WAYS DICTATION CAN FAIL, separated October 13, 2026. Until then
  // every failure became REFUSED and said "isn't available in this browser",
  // which was untrue for the ones that would pass on a second try: Dennis saw
  // that message with the network off before first use, when the only problem
  // was that the language pack could not be fetched at that moment.
  REFUSED: 'refused',         // permanent for this browser — no retry will help
  RETRY: 'retry',             // failed this time, may well work next time
  PERMISSION_DENIED: 'permission-denied' // the microphone was refused, not dictation
};

/**
 * The one refusal message, worded by Dennis on October 12, 2026. Exported so the
 * button, the sidebar and the extension's copy cannot drift from each other.
 */
export const DICTATION_REFUSED_MESSAGE =
  "Dictation isn't available in this browser. CKG only transcribes speech on " +
  "your device, and this browser can't do that. You can type instead.";

/**
 * Shown for a failure that may pass on a retry — the language pack could not be
 * installed just now, starting failed for some other reason, an unexpected
 * exception. The microphone stays usable, and a retry runs every check again.
 */
export const DICTATION_RETRY_MESSAGE =
  "Dictation couldn't start just now. You can try again or type instead.";

/**
 * Shown when the microphone itself was denied. Distinct from both of the above:
 * nothing is wrong with dictation, and the user can fix it.
 */
export const DICTATION_PERMISSION_MESSAGE =
  'CKG needs microphone permission to dictate. You can allow it in your ' +
  "browser's site settings, or type instead.";

/** Shown while a language pack downloads (ruling point 8). */
export const DICTATION_PREPARING_MESSAGE =
  'Preparing on-device dictation — downloading the language pack...';

// =========================================
// Browser Support Detection
// =========================================

/**
 * Check if the Web Speech API is available in this browser
 * @returns {boolean}
 */
const checkBrowserSupport = () => {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

/**
 * Get the SpeechRecognition constructor (handles vendor prefixes)
 * @returns {SpeechRecognition|null}
 */
const getSpeechRecognitionClass = () => {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

/**
 * REQUIRE LOCAL PROCESSING ON THE RECOGNITION OBJECT ITSELF.
 *
 * This is the instruction that actually binds the browser. Everything else —
 * the static `available()` and `install()` calls — only asks whether on-device
 * recognition *could* happen; it does not ask for it. Before this function
 * existed, both code paths set `recognition.localService = true`, which does
 * nothing: `localService` is a property of SpeechSynthesisVoice, not of
 * SpeechRecognition, so assigning it created an ordinary own property that the
 * browser never reads, and recognition ran against the cloud recognizer while
 * the UI said "private, on-device".
 *
 * THE ATTRIBUTE: `processLocally`, a boolean on the SpeechRecognition instance,
 * default false. It is the instance-level counterpart of the `processLocally`
 * option the static `available()` / `install()` methods in this same Chrome
 * on-device Web Speech API extension already take — those are the methods this
 * code has been calling since the feature was added. With it true, a conforming
 * user agent must recognize on-device and must raise an error rather than fall
 * back to a remote service.
 *
 * HOW CONFIDENT I AM, PLAINLY: reasonably, not certainly. `processLocally` is
 * the instance attribute in that extension and is the counterpart of the option
 * name already in use here, but this was written without a browser to test
 * against, so it has not been observed working. Two things make that
 * uncertainty safe rather than load-bearing:
 *
 *   1. The attribute must be PRESENT on the instance for dictation to be
 *      permitted at all (the `'processLocally' in recognition` test below). A
 *      browser that does not implement it is refused rather than trusted. So if
 *      the name is wrong, dictation is refused everywhere — which is the safe
 *      direction, and is immediately visible rather than silent.
 *   2. A `network` error during recognition is treated as proof that audio left
 *      the device, and hard-refuses (see the onerror handler). On-device
 *      recognition cannot produce a network error. That check does not depend on
 *      the attribute name being right at all.
 *
 * The read-back below is deliberately NOT treated as proof the browser honours
 * the request: on a browser without the attribute, assignment would create a
 * plain own property that reads back true. That is exactly why the `in` test
 * comes first — `in` consults the prototype chain, so it is false before the
 * assignment on a browser that lacks the attribute.
 *
 * @param {SpeechRecognition} recognition
 * @returns {boolean} true only when the instance accepts the requirement
 */
const requireLocalProcessing = (recognition) => {
  if (!recognition) return false;

  // Prototype-chain test FIRST, before any assignment of our own.
  if (!('processLocally' in recognition)) {
    return false;
  }

  try {
    recognition.processLocally = true;
  } catch (error) {
    // A read-only or throwing setter means we cannot impose the requirement.
    return false;
  }

  return recognition.processLocally === true;
};

// =========================================
// Main Hook
// =========================================

const useSpeechRecognition = () => {
  // =========================================
  // State
  // =========================================
  
  // Core recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true); // Master toggle
  const [isSupported, setIsSupported] = useState(() => checkBrowserSupport());
  
  // Permission and status
  const [permissionState, setPermissionState] = useState(PERMISSION_STATES.UNKNOWN);
  const [statusType, setStatusType] = useState(STATUS_TYPES.IDLE);
  const [statusMessage, setStatusMessage] = useState('');
  
  // On-device recognition verdict. UNKNOWN until probed, and UNKNOWN does not
  // permit dictation — the default is refusal.
  const [onDeviceState, setOnDeviceState] = useState(ON_DEVICE_STATES.UNKNOWN);

  // Kept for the "private, on-device" labels. It is now DERIVED from the
  // verdict rather than set independently, so a label can no longer claim
  // on-device while recognition runs against the cloud (ruling point 7).
  const isOnDeviceRecognition = onDeviceState === ON_DEVICE_STATES.CONFIRMED;

  // True when a recognition object may be started. RETRY and PERMISSION_DENIED
  // are included deliberately: both are states the user is invited to try
  // again from, and a retry runs the whole gate afresh.
  const isDictationAvailable =
    onDeviceState === ON_DEVICE_STATES.CONFIRMED ||
    onDeviceState === ON_DEVICE_STATES.INSTALLABLE ||
    onDeviceState === ON_DEVICE_STATES.RETRY ||
    onDeviceState === ON_DEVICE_STATES.PERMISSION_DENIED;

  // True ONLY for the permanent refusal. This is what disables the microphone
  // control, so it must not include the retryable states — leaving the control
  // usable is the whole point of distinguishing them (October 13, 2026).
  const isDictationRefused = onDeviceState === ON_DEVICE_STATES.REFUSED;

  // =========================================
  // Refs (persist across renders without causing re-renders)
  // =========================================
  
  // The SpeechRecognition instance
  const recognitionRef = useRef(null);
  
  // Current target textarea reference
  const targetRef = useRef(null);
  
  // Current character limit (null = no limit)
  const charLimitRef = useRef(null);
  
  // Base text tracker (text that existed before recording started + finalized transcripts)
  const baseTextRef = useRef('');
  
  // Callback for when text changes (allows parent to update React state)
  const onTranscriptChangeRef = useRef(null);
  
  // Flag to track if we intentionally stopped (vs browser auto-stop)
  const intentionalStopRef = useRef(false);

  // True while a start attempt is in flight. The gate is asynchronous, so two
  // quick clicks would otherwise both run it; the second must do nothing rather
  // than fail (point 5, October 13, 2026).
  const startInFlightRef = useRef(false);

  // The verdict, mirrored into a ref so the recognition event handlers can read
  // it without being re-created. onend's auto-restart consults this: a restart
  // is a start, and no start is permitted without a confirmed verdict.
  const onDeviceConfirmedRef = useRef(false);

  // =========================================
  // Permission Checking
  // =========================================

  /**
   * Check the current microphone permission state
   * Updates permissionState accordingly
   */
  const checkPermission = useCallback(async () => {
    try {
      // Try the Permissions API first (more reliable)
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        setPermissionState(result.state);
        
        // Listen for permission changes
        result.addEventListener('change', () => {
          setPermissionState(result.state);
        });
        
        return result.state;
      }
    } catch (error) {
      // Permissions API not available (e.g., Firefox)
      console.log('Permissions API not available:', error.message);
    }
    
    // Fallback: we don't know the state until they try to use it
    setPermissionState(PERMISSION_STATES.UNKNOWN);
    return PERMISSION_STATES.UNKNOWN;
  }, []);

  // Check permission on mount
  useEffect(() => {
    if (isSupported) {
      checkPermission();
    }
  }, [isSupported, checkPermission]);

  // =========================================
  // On-device capability
  // =========================================

  /**
   * Drop any recognition instance we hold. Shared by all three failure paths:
   * none of them may leave a startable object behind.
   */
  const discardRecognition = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        // Already stopped, or never started.
      }
      recognitionRef.current = null;
    }
  }, []);

  /**
   * REFUSE DICTATION PERMANENTLY. For failures that will not change by trying
   * again in this browser:
   *
   *   - the Web Speech API is absent
   *   - the recognition instance has no `processLocally` attribute
   *   - on-device recognition is reported unavailable for the language
   *   - a network error occurred during recognition (the hard refusal — proof
   *     that audio left the device; no retry in this session)
   *
   * Sets REFUSED, which disables the microphone, and shows
   * DICTATION_REFUSED_MESSAGE.
   *
   * @param {string} reason - console only; never shown to the user
   */
  const refuseDictation = useCallback((reason) => {
    console.log('Dictation refused (permanent):', reason);
    onDeviceConfirmedRef.current = false;
    setOnDeviceState(ON_DEVICE_STATES.REFUSED);
    setIsRecording(false);
    setStatusType(STATUS_TYPES.ERROR);
    setStatusMessage(DICTATION_REFUSED_MESSAGE);
    discardRecognition();
  }, [discardRecognition]);

  /**
   * A FAILURE THAT MAY PASS NEXT TIME. For the ones that are about this moment
   * rather than about this browser:
   *
   *   - install() threw, or returned anything but true (the pack could not be
   *     fetched — offline, or the download was interrupted)
   *   - start() threw for a reason other than an in-flight start
   *   - an unexpected exception anywhere in the check
   *
   * Leaves the microphone USABLE and the verdict unconfirmed, so the next click
   * runs the whole gate again from the beginning — the probe, the attribute
   * requirement, the availability check, the install. Nothing is remembered
   * that could let a retry skip a check.
   *
   * @param {string} reason - console only; never shown to the user
   */
  const failTransiently = useCallback((reason) => {
    console.log('Dictation could not start this time:', reason);
    onDeviceConfirmedRef.current = false;
    setOnDeviceState(ON_DEVICE_STATES.RETRY);
    setIsRecording(false);
    setStatusType(STATUS_TYPES.ERROR);
    setStatusMessage(DICTATION_RETRY_MESSAGE);
    discardRecognition();
  }, [discardRecognition]);

  /**
   * THE MICROPHONE WAS DENIED. Not a dictation failure at all — the feature
   * works, the user has not granted access to the hardware. Says so, and says
   * where to change it. The microphone control stays usable so that granting
   * permission and clicking again works without a reload.
   */
  const failOnPermission = useCallback(() => {
    console.log('Dictation blocked: microphone permission denied');
    onDeviceConfirmedRef.current = false;
    setPermissionState(PERMISSION_STATES.DENIED);
    setOnDeviceState(ON_DEVICE_STATES.PERMISSION_DENIED);
    setIsRecording(false);
    setStatusType(STATUS_TYPES.ERROR);
    setStatusMessage(DICTATION_PERMISSION_MESSAGE);
    discardRecognition();
  }, [discardRecognition]);

  /**
   * Probe whether on-device recognition can be guaranteed, WITHOUT downloading
   * anything. Runs once on mount so the microphone control can be rendered
   * disabled-with-a-reason before the user clicks it, rather than appearing to
   * work and then refusing.
   *
   * Deliberately never calls install(): a language-pack download is a
   * user-visible action and is only started from a click (see startRecording).
   *
   * FAILS CLOSED at every branch. The verdict is only CONFIRMED or INSTALLABLE
   * on an explicit positive answer; everything else — a missing API, a missing
   * instance attribute, 'unavailable', an unrecognized result, or any thrown
   * error — refuses.
   */
  const probeOnDeviceSupport = useCallback(async () => {
    const SpeechRecognitionClass = getSpeechRecognitionClass();

    if (!SpeechRecognitionClass) {
      setIsSupported(false);
      refuseDictation('Web Speech API absent');
      return;
    }

    setOnDeviceState(ON_DEVICE_STATES.CHECKING);

    try {
      // (a) The instance must accept the local-processing requirement. Checked
      // on a throwaway instance — constructing one prompts for nothing; only
      // start() does.
      const probe = new SpeechRecognitionClass();
      if (!requireLocalProcessing(probe)) {
        refuseDictation('recognition instance does not support processLocally');
        return;
      }

      // (b) The static availability API must exist. Without it we cannot ask
      // whether the language can be recognized locally, and an unanswerable
      // question is a refusal, not a green light.
      if (!('available' in SpeechRecognitionClass)) {
        refuseDictation('SpeechRecognition.available() absent');
        return;
      }

      const lang = navigator.language || 'en-US';
      const availability = await SpeechRecognitionClass.available({
        langs: [lang],
        processLocally: true
      });

      if (availability === 'available') {
        onDeviceConfirmedRef.current = true;
        setOnDeviceState(ON_DEVICE_STATES.CONFIRMED);
        return;
      }

      if (availability === 'downloadable' || availability === 'downloading') {
        // Installable, not yet confirmed. The pack is fetched on the user's
        // first click so the download is something they asked for.
        setOnDeviceState(ON_DEVICE_STATES.INSTALLABLE);
        return;
      }

      // 'unavailable', and anything this code does not recognize.
      refuseDictation(`availability reported '${availability}'`);
    } catch (error) {
      // TRANSIENT, not permanent: an exception here is usually the call not
      // completing (offline, a slow or interrupted platform check), which a
      // retry can pass. A definite 'unavailable' answer is the permanent case
      // and is handled above.
      failTransiently(`availability check threw: ${error?.message}`);
    }
  }, [refuseDictation, failTransiently]);

  // Probe once on mount.
  useEffect(() => {
    probeOnDeviceSupport();
  }, [probeOnDeviceSupport]);

  // =========================================
  // Recognition Initialization
  // =========================================

  /**
   * Build a SpeechRecognition instance that is REQUIRED to process locally, or
   * return null and refuse.
   *
   * The order matters and is the whole of the fix. Previously this function
   * created the object, asked politely whether on-device was possible, ignored
   * every negative answer, and returned a startable object regardless. Now
   * nothing is returned unless the requirement is on the instance and the
   * language is confirmed recognizable locally.
   *
   * @returns {Promise<SpeechRecognition|null>} null means dictation is refused
   */
  const initializeRecognition = useCallback(async () => {
    const SpeechRecognitionClass = getSpeechRecognitionClass();

    if (!SpeechRecognitionClass) {
      setIsSupported(false);
      refuseDictation('Web Speech API absent');
      return null;
    }

    const recognition = new SpeechRecognitionClass();

    // Configure recognition
    recognition.continuous = true;      // Keep listening until stopped
    recognition.interimResults = true;  // Show results as user speaks
    recognition.lang = navigator.language || 'en-US';

    // =========================================
    // THE REQUIREMENT, BEFORE ANYTHING ELSE
    // =========================================
    // No recognition object may be started without this (ruling point 3). It is
    // set before the availability check so that a browser lacking the attribute
    // is refused without a round trip.
    if (!requireLocalProcessing(recognition)) {
      refuseDictation('recognition instance does not support processLocally');
      return null;
    }

    // =========================================
    // Confirm the language can be recognized on-device
    // =========================================
    try {
      if (!('available' in SpeechRecognitionClass)) {
        refuseDictation('SpeechRecognition.available() absent');
        return null;
      }

      setStatusType(STATUS_TYPES.INITIALIZING);
      setStatusMessage('Checking on-device dictation...');
      setOnDeviceState(ON_DEVICE_STATES.CHECKING);

      const availability = await SpeechRecognitionClass.available({
        langs: [recognition.lang],
        processLocally: true
      });

      if (availability === 'available') {
        onDeviceConfirmedRef.current = true;
        setOnDeviceState(ON_DEVICE_STATES.CONFIRMED);

      } else if (availability === 'downloadable' || availability === 'downloading') {
        // Ruling point 8: say what is happening while the pack downloads.
        setOnDeviceState(ON_DEVICE_STATES.PREPARING);
        setStatusType(STATUS_TYPES.INITIALIZING);
        setStatusMessage(DICTATION_PREPARING_MESSAGE);

        let installed = false;
        try {
          installed = await SpeechRecognitionClass.install({
            langs: [recognition.lang],
            processLocally: true
          });
        } catch (installError) {
          // TRANSIENT: the pack could not be fetched just now. This is the
          // exact case Dennis hit with the network off before first use.
          failTransiently(`install() threw: ${installError?.message}`);
          return null;
        }

        // install() returning nothing is a failure, not a maybe.
        if (installed !== true) {
          // TRANSIENT, same reason: the download did not complete.
          failTransiently(`install() returned ${JSON.stringify(installed)}`);
          return null;
        }

        onDeviceConfirmedRef.current = true;
        setOnDeviceState(ON_DEVICE_STATES.CONFIRMED);

      } else {
        // 'unavailable', and every result this code does not know.
        refuseDictation(`availability reported '${availability}'`);
        return null;
      }
    } catch (error) {
      // TRANSIENT, as in the probe above.
      failTransiently(`availability check threw: ${error?.message}`);
      return null;
    }

    setStatusType(STATUS_TYPES.IDLE);
    setStatusMessage('');

    // =========================================
    // Event Handlers
    // =========================================

    recognition.onstart = () => {
      console.log('Speech recognition started');
      setIsRecording(true);
      setStatusType(STATUS_TYPES.RECORDING);
      // Reading the REF, not the state: this handler is created once and the
      // state value in its closure can be stale, which is exactly how a label
      // could claim on-device for a session that was not. Recognition can only
      // reach this point with a confirmed verdict, so the plain "Listening..."
      // branch should be unreachable — it is kept as the honest fallback rather
      // than asserting the label unconditionally (ruling point 7).
      setStatusMessage(
        onDeviceConfirmedRef.current
          ? 'Listening... (private, on-device)'
          : 'Listening...'
      );
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      // Process results
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // If we have final results, add them to base text
      if (finalTranscript) {
        const separator = baseTextRef.current && !baseTextRef.current.endsWith(' ') ? ' ' : '';
        baseTextRef.current = baseTextRef.current + separator + finalTranscript;
      }

      // Combine base text + interim text
      const separator = baseTextRef.current && !baseTextRef.current.endsWith(' ') && interimTranscript ? ' ' : '';
      let fullText = baseTextRef.current + separator + interimTranscript;

      // =========================================
      // Character Limit Enforcement
      // =========================================
      const charLimit = charLimitRef.current;
      if (charLimit && fullText.length >= charLimit) {
        // Truncate to limit
        fullText = fullText.slice(0, charLimit);
        baseTextRef.current = fullText;
        
        // Stop recording - we've hit the limit
        console.log(`Character limit (${charLimit}) reached, stopping recording`);
        intentionalStopRef.current = true;
        recognition.stop();
        
        setStatusType(STATUS_TYPES.IDLE);
        setStatusMessage('Character limit reached');
        
        // Clear the "limit reached" message after 3 seconds
        setTimeout(() => {
          setStatusMessage('');
        }, 3000);
      }

      // Update the target textarea
      if (targetRef.current) {
        targetRef.current.value = fullText;
        
        // Trigger input event so React state updates if needed
        const inputEvent = new Event('input', { bubbles: true });
        targetRef.current.dispatchEvent(inputEvent);
      }

      // Call the transcript change callback if provided
      if (onTranscriptChangeRef.current) {
        onTranscriptChangeRef.current(fullText);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);

      switch (event.error) {
        case 'not-allowed':
        case 'permission-denied':
          // THE MICROPHONE WAS DENIED — not a dictation failure. Says what is
          // wrong and where to fix it, and leaves the control usable so that
          // granting permission and clicking again works without a reload.
          stopRecordingInternal();
          failOnPermission();
          break;
          
        case 'no-speech':
          // Silence detected - this is normal, just keep listening
          console.log('No speech detected, continuing to listen...');
          // Don't change status - keep showing "Listening..."
          break;
          
        case 'network':
          // A NETWORK ERROR IS PROOF THAT AUDIO LEFT THE DEVICE, or that the
          // browser tried to send it. On-device recognition has nothing to
          // reach over the network and cannot raise this. So this is not a
          // connectivity message to the user — it is a breach of the ruling,
          // and it hard-refuses.
          //
          // This check is the backstop that does not depend on the
          // `processLocally` attribute name being correct (see
          // requireLocalProcessing). If the attribute is wrong or unimplemented
          // and the browser silently uses its cloud recognizer, this is what
          // catches it — on the first attempt, before a second utterance.
          refuseDictation('network error during recognition — audio left the device');
          stopRecordingInternal();
          break;

        case 'audio-capture':
          setStatusType(STATUS_TYPES.ERROR);
          setStatusMessage('No microphone found.');
          stopRecordingInternal();
          break;

        case 'language-not-supported':
        case 'service-not-allowed':
          // With processLocally true, this is how a conforming user agent says
          // it cannot recognize this language locally. The old code showed
          // "Language not supported." and left dictation enabled; under the
          // ruling the correct response is the refusal, and no retry.
          refuseDictation(`recognition reported '${event.error}' under processLocally`);
          stopRecordingInternal();
          break;
          
        case 'aborted':
          // User or system aborted - not really an error
          console.log('Speech recognition aborted');
          break;
          
        default:
          setStatusType(STATUS_TYPES.ERROR);
          setStatusMessage('Voice input error. Please try again.');
      }
    };

    recognition.onend = () => {
      console.log('Speech recognition ended');

      // If we're still supposed to be recording and didn't intentionally stop,
      // restart (handles browser auto-stop after silence).
      //
      // A RESTART IS A START, so it carries the same precondition (ruling
      // point 3): the verdict must still be confirmed, and the instance must
      // still carry the requirement. Both are re-checked rather than assumed,
      // because this runs long after initialization and the object is
      // long-lived.
      if (isRecording && !intentionalStopRef.current) {
        if (!onDeviceConfirmedRef.current || recognition.processLocally !== true) {
          refuseDictation('auto-restart blocked: local processing not guaranteed');
          stopRecordingInternal();
          return;
        }
        try {
          console.log('Restarting recognition (browser auto-stopped)');
          recognition.start();
        } catch (error) {
          console.log('Could not restart recognition:', error);
          stopRecordingInternal();
        }
      } else {
        // Clean up
        setIsRecording(false);
        if (statusType === STATUS_TYPES.RECORDING) {
          setStatusType(STATUS_TYPES.IDLE);
          setStatusMessage('');
        }
      }
    };

    return recognition;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refuseDictation, failTransiently, failOnPermission]); // isRecording and statusType accessed via state, intentional to avoid re-init;
                         // the on-device verdict is read from onDeviceConfirmedRef, not from state,
                         // so it is no longer a dependency and no longer re-creates the instance.

  /**
   * Internal stop function (doesn't reset intentionalStopRef)
   */
  const stopRecordingInternal = useCallback(() => {
    setIsRecording(false);
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.log('Error stopping recognition:', error);
      }
    }
    
    // Clear target
    targetRef.current = null;
    charLimitRef.current = null;
    baseTextRef.current = '';
    onTranscriptChangeRef.current = null;
  }, []);

  // =========================================
  // Public API
  // =========================================

  /**
   * Start recording speech to a target textarea
   * 
   * @param {React.RefObject<HTMLTextAreaElement>} textareaRef - Ref to the target textarea
   * @param {number|null} maxLength - Maximum character length (null for no limit)
   * @param {function|null} onTranscriptChange - Optional callback when transcript changes
   */
  const startRecording = useCallback(async (textareaRef, maxLength = null, onTranscriptChange = null) => {
    // Check if enabled
    if (!isEnabled) {
      console.log('Speech recognition is disabled');
      return false;
    }

    // Check browser support
    if (!isSupported) {
      setStatusType(STATUS_TYPES.ERROR);
      setStatusMessage(DICTATION_REFUSED_MESSAGE);
      return false;
    }

    // DOUBLE CLICK: a start is already in flight, so do nothing at all — no
    // message, no refusal, no second recognition object (point 5, October 13,
    // 2026). The gate is asynchronous (an availability check, sometimes a
    // download), so two quick clicks used to reach start() twice: the second
    // threw InvalidStateError and, until today, that was turned into a
    // permanent refusal. The flag is cleared in the finally below, so it cannot
    // wedge if the attempt throws.
    if (startInFlightRef.current) {
      console.log('Ignoring click: a dictation start is already in flight');
      return false;
    }

    // Already-established PERMANENT refusal: say so again and stop here. No
    // probing, no retrying, no recognition object. RETRY and PERMISSION_DENIED
    // deliberately fall through — they are the states a retry is meant to pass.
    if (onDeviceState === ON_DEVICE_STATES.REFUSED) {
      setStatusType(STATUS_TYPES.ERROR);
      setStatusMessage(DICTATION_REFUSED_MESSAGE);
      return false;
    }

    startInFlightRef.current = true;

    // If already recording to a different target, stop first
    if (isRecording) {
      console.log('Stopping existing recording before starting new one');
      intentionalStopRef.current = true;
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      // Small delay to let the stop complete
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Set up the new target
    targetRef.current = textareaRef?.current || null;
    charLimitRef.current = maxLength;
    onTranscriptChangeRef.current = onTranscriptChange;
    
    // Capture the current text in the textarea as the base
    baseTextRef.current = targetRef.current?.value || '';
    
    // Reset the intentional stop flag
    intentionalStopRef.current = false;

    try {
      // Initialize recognition if needed
      if (!recognitionRef.current) {
        recognitionRef.current = await initializeRecognition();
      }

      if (!recognitionRef.current) {
        // initializeRecognition already called refuseDictation and set the
        // message. Do not overwrite it with a vaguer one.
        return false;
      }

      // LAST GATE BEFORE start(). Both conditions re-checked on the actual
      // object we are about to start, because the one above may have come from
      // a previous call and this is the only line that matters.
      if (
        !onDeviceConfirmedRef.current ||
        recognitionRef.current.processLocally !== true
      ) {
        refuseDictation('start blocked: local processing not guaranteed');
        return false;
      }

      // Start recognition
      setStatusType(STATUS_TYPES.INITIALIZING);
      setStatusMessage('Starting...');

      recognitionRef.current.start();
      return true;

    } catch (error) {
      // A FAILED start() IS TREATED AS TRANSIENT, revised October 13, 2026.
      //
      // It used to refuse permanently, on the reasoning that the browser does
      // not say whether local processing was the reason and fail-closed was the
      // safe default. That is still true of the *recognition*, but it was the
      // wrong thing to tell the user: a start can fail for reasons that have
      // nothing to do with on-device support, and "isn't available in this
      // browser" was then false. Safety does not depend on the message — it
      // depends on the gate, and the gate is unchanged: failTransiently clears
      // the confirmed verdict and discards the instance, so the next click runs
      // every check again from the beginning. Nothing can start without the
      // requirement either way.
      //
      // InvalidStateError is NOT a failure. It means start() was called on a
      // recognition that is already starting or started — the double-click case
      // — and the correct response is to do nothing and let the first start
      // proceed (point 5: a double click must not produce a refusal).
      if (error && error.name === 'InvalidStateError') {
        console.log('start() ignored: recognition already starting');
        return false;
      }

      console.error('Failed to start speech recognition:', error);
      failTransiently(`start() threw: ${error?.message}`);
      return false;

    } finally {
      // Always cleared, on every path out of the attempt — success, refusal,
      // transient failure or throw. A flag that can stay set is a flag that
      // disables the microphone until reload.
      startInFlightRef.current = false;
    }
  }, [
    isEnabled,
    isSupported,
    isRecording,
    onDeviceState,
    initializeRecognition,
    refuseDictation,
    failTransiently
  ]);

  /**
   * Stop recording
   */
  const stopRecording = useCallback(() => {
    intentionalStopRef.current = true;
    baseTextRef.current = '';
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.log('Error stopping recognition:', error);
      }
    }
    
    setIsRecording(false);
    setStatusType(STATUS_TYPES.IDLE);
    setStatusMessage('');
    
    // Clear refs
    targetRef.current = null;
    charLimitRef.current = null;
    onTranscriptChangeRef.current = null;
  }, []);

  /**
   * Toggle recording on/off
   * Convenience method for mic button click handlers
   * 
   * @param {React.RefObject<HTMLTextAreaElement>} textareaRef - Ref to the target textarea
   * @param {number|null} maxLength - Maximum character length (null for no limit)
   * @param {function|null} onTranscriptChange - Optional callback when transcript changes
   */
  const toggleRecording = useCallback(async (textareaRef, maxLength = null, onTranscriptChange = null) => {
    if (isRecording) {
      stopRecording();
    } else {
      await startRecording(textareaRef, maxLength, onTranscriptChange);
    }
  }, [isRecording, startRecording, stopRecording]);

  /**
   * Toggle the master enable/disable switch
   * Used by the sidebar toggle
   */
  const toggleEnabled = useCallback(() => {
    setIsEnabled(prev => {
      const newValue = !prev;
      
      // If disabling while recording, stop the recording
      if (!newValue && isRecording) {
        stopRecording();
      }
      
      return newValue;
    });
  }, [isRecording, stopRecording]);

  /**
   * Set the enabled state directly
   * @param {boolean} enabled
   */
  const setEnabled = useCallback((enabled) => {
    setIsEnabled(enabled);
    
    // If disabling while recording, stop the recording
    if (!enabled && isRecording) {
      stopRecording();
    }
  }, [isRecording, stopRecording]);

  /**
   * Request microphone permission explicitly
   * Useful for a "Set up microphone" flow
   */
  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Permission granted - stop the stream immediately (we don't need it)
      stream.getTracks().forEach(track => track.stop());
      
      setPermissionState(PERMISSION_STATES.GRANTED);
      return true;
      
    } catch (error) {
      console.error('Microphone permission error:', error);
      
      if (error.name === 'NotAllowedError') {
        setPermissionState(PERMISSION_STATES.DENIED);
      }
      
      return false;
    }
  }, []);

  /**
   * Clear any error status
   */
  const clearStatus = useCallback(() => {
    setStatusType(STATUS_TYPES.IDLE);
    setStatusMessage('');
  }, []);

  // =========================================
  // Cleanup on unmount
  // =========================================
  
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          // Ignore errors during cleanup
        }
        recognitionRef.current = null;
      }
    };
  }, []);

  // =========================================
  // Return Public API
  // =========================================
  
  return {
    // State
    isRecording,
    isEnabled,
    isSupported,
    isOnDeviceRecognition,   // derived from the verdict; true only when CONFIRMED
    onDeviceState,           // the verdict itself, for a UI that needs the detail
    isDictationAvailable,    // may a recognition object be started at all
    isDictationRefused,      // render the control disabled, with the message
    permissionState,
    statusType,
    statusMessage,

    // Actions
    startRecording,
    stopRecording,
    toggleRecording,
    toggleEnabled,
    setEnabled,
    requestPermission,
    clearStatus
  };
};

export default useSpeechRecognition;
